import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { FertilizationBalanceSetting, SpecificReferenceBlock } from 'src/app/shared/models';

export type SpecificReferenceBlockDialogResult =
  | { action: 'save'; data: SpecificReferenceBlock }
  | { action: 'remove' }
  | { action: 'cancel' };

export interface SpecificReferenceBlockDialogData {
  fertilizationBalanceSetting: FertilizationBalanceSetting;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-fertilization-balance-settings-specific-reference-block-dialog',
  templateUrl:
    './specific-reference-block-dialog.component.html',
  styleUrl:
    './specific-reference-block-dialog.component.scss',
  standalone: false,
})
export class SpecificReferenceBlockDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<SpecificReferenceBlockDialogComponent, SpecificReferenceBlockDialogResult>>(MatDialogRef);
  data = inject<SpecificReferenceBlockDialogData>(MAT_DIALOG_DATA);

  private readonly destroy$ = new Subject<void>();
  private readonly stopTyping$ = new Subject<void>();
  isTyping = false;

  readonly form = new FormGroup(
    {
      longitude: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(-180),
        Validators.max(180),
        this.finiteCoordinateValidator,
      ]),
      latitude: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(-90),
        Validators.max(90),
        this.finiteCoordinateValidator,
      ]),
      remarks: new FormControl<string>('', [
        Validators.maxLength(256),
        this.mobilePhoneValidator.bind(this),
      ]),
    },
  );

  get isRemoval(): boolean {
    return this.data.mode === 'edit'
      && this.form.controls.longitude.value == null
      && this.form.controls.latitude.value == null;
  }

  get canConfirm(): boolean {
    return this.isRemoval || this.form.valid;
  }

  private finiteCoordinateValidator(control: AbstractControl): ValidationErrors | null {
    return control.value != null && !Number.isFinite(control.value)
      ? { invalidCoordinate: true }
      : null;
  }

  private mobilePhoneValidator(control: AbstractControl): ValidationErrors | null {
    const mobileNumber = control.value;
    const existingRemarks = this.data.fertilizationBalanceSetting.specificReferenceBlockData?.remarks;

    if (typeof mobileNumber !== 'string' || mobileNumber.trim().length === 0 || mobileNumber === existingRemarks) {
      return null;
    }

    const normalizedMobileNumber = mobileNumber.replace(/[\s-]/g, '');
    return /^(?:06\d{8}|\+316\d{8})$/.test(normalizedMobileNumber)
      ? null
      : { invalidMobilePhone: true };
  }

  constructor() {
    const specificReferenceBlockData = this.data.fertilizationBalanceSetting?.specificReferenceBlockData;
    if (specificReferenceBlockData) {
      this.form.patchValue({
        longitude: specificReferenceBlockData.longitude ?? null,
        latitude: specificReferenceBlockData.latitude ?? null,
        remarks: specificReferenceBlockData.remarks ?? '',
      });
    }
  }

  ngOnInit(): void {
    // Start animation on any form change
    this.form.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.isTyping = true;
      this.stopTyping$.next();
    });

    // Stop animation after user stops typing for 600ms
    this.stopTyping$.pipe(
      debounceTime(600),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.isTyping = false;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirm() {
    const { longitude, latitude, remarks } = this.form.getRawValue();
    if (this.isRemoval) {
      this.dialogRef.close({ action: 'remove' });
    } else if (this.form.valid && longitude != null && latitude != null) {
      this.dialogRef.close({
        action: 'save',
        data: { longitude, latitude, remarks: remarks ?? '' },
      });
    }
  }

  cancel() {
    this.dialogRef.close({ action: 'cancel' });
  }

}
